import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BACKEND_ENDPOINTS } from "../config/backend";
import { useCatalog } from "../context/CatalogContext.jsx";
import { clearAuthToken, getAuthToken } from "../lib/auth.js";

const buildUrl = (baseUrl, path) => `${baseUrl ?? ""}${path}`;

const buildAlertClass = (type) => {
  switch (type) {
    case "success":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "error":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "info":
    default:
      return "bg-sky-50 text-sky-700 border-sky-200";
  }
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return "{}";
  }
};

export default function AdminCatalogPage() {
  const { catalog, loading, error, refresh, baseUrl } = useCatalog();
  const serializedCatalog = useMemo(() => safeStringify(catalog), [catalog]);
  const apiBase = baseUrl ?? "";

  const [editorValue, setEditorValue] = useState(serializedCatalog);
  const [hasEdited, setHasEdited] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const currentUserUrl = buildUrl(apiBase, BACKEND_ENDPOINTS.currentUser);
  const loginUrl = `${buildUrl(apiBase, BACKEND_ENDPOINTS.login)}?redirect_to=${encodeURIComponent("/admin")}`;
  const logoutUrl = buildUrl(apiBase, BACKEND_ENDPOINTS.logout);
  const catalogUrl = buildUrl(apiBase, BACKEND_ENDPOINTS.catalog);

  const fetchCurrentUser = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);

    const token = getAuthToken();
    if (!token) {
      clearAuthToken();
      setAuthUser(null);
      setAuthLoading(false);
      return;
    }

    try {
      const response = await fetch(currentUserUrl, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        clearAuthToken();
        setAuthUser(null);
        setAuthLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      setAuthUser(payload);
    } catch (fetchError) {
      setAuthUser(null);
      setAuthError(
        fetchError instanceof Error ? fetchError.message : String(fetchError)
      );
    } finally {
      setAuthLoading(false);
    }
  }, [currentUserUrl]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      await fetchCurrentUser();
      if (cancelled) {
        setAuthLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (!hasEdited) {
      setEditorValue(serializedCatalog);
    }
  }, [serializedCatalog, hasEdited]);

  const handleEditorChange = (event) => {
    if (!hasEdited) {
      setHasEdited(true);
    }
    setEditorValue(event.target.value);
  };

  const handleReset = () => {
    setEditorValue(serializedCatalog);
    setHasEdited(false);
    setStatus(null);
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorValue);
      setEditorValue(JSON.stringify(parsed, null, 2));
      setStatus({ type: "success", message: "JSON formateado correctamente." });
    } catch (formatError) {
      setStatus({
        type: "error",
        message: `JSON inválido: ${formatError instanceof Error ? formatError.message : String(formatError)}`,
      });
    }
  };

  const handleLogout = async () => {
    setStatus(null);
    const token = getAuthToken();

    if (token) {
      try {
        const response = await fetch(logoutUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        setStatus({ type: "success", message: "Sesión cerrada correctamente." });
      } catch (logoutError) {
        setStatus({
          type: "error",
          message: `No se pudo cerrar sesión: ${logoutError instanceof Error ? logoutError.message : String(logoutError)}`,
        });
      }
    } else {
      setStatus({ type: "success", message: "Sesión cerrada correctamente." });
    }

    clearAuthToken();
    setAuthUser(null);
    setHasEdited(false);
    setAuthLoading(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);

    const token = getAuthToken();

    if (!authUser || !token) {
      setStatus({
        type: "error",
        message: "Debes iniciar sesión para actualizar el catálogo.",
      });
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(editorValue);
    } catch (parseError) {
      setStatus({ type: "error", message: `JSON inválido: ${parseError instanceof Error ? parseError.message : String(parseError)}` });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(catalogUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(parsed),
      });

      if (!response.ok) {
        let message = `No se pudo guardar el catálogo (HTTP ${response.status}).`;
        try {
          const payload = await response.json();
          if (payload && typeof payload.message === "string") {
            message = payload.message;
          }
        } catch (_) {
          // no-op
        }
        throw new Error(message);
      }

      setStatus({ type: "success", message: "Catálogo actualizado correctamente." });
      setHasEdited(false);
      refresh();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : String(submitError);
      setStatus({ type: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeaderControls = () => {
    if (authUser) {
      return (
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <div className="flex flex-col text-right">
            <span className="font-medium text-white">{authUser.name}</span>
            <span className="text-xs text-slate-400">{authUser.email}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-slate-700 px-3 py-1 text-slate-200 transition hover:border-slate-500 hover:text-white"
          >
            Cerrar sesión
          </button>
        </div>
      );
    }

    return (
      <a
        className="rounded border border-emerald-500/60 px-3 py-1 text-sm font-medium text-emerald-200 transition hover:border-emerald-400 hover:text-white"
        href={loginUrl}
      >
        Iniciar sesión con Google
      </a>
    );
  };

  const renderContent = () => {
    if (authLoading && !authUser) {
      return (
        <div className="rounded border border-slate-700 bg-slate-800 px-4 py-6 text-sm text-slate-300">
          Verificando sesión...
        </div>
      );
    }

    if (!authUser) {
      return (
        <div className="flex flex-col items-start gap-4 rounded-lg border border-slate-800 bg-slate-900/60 p-6 shadow-lg shadow-slate-950/30">
          <div>
            <h2 className="text-lg font-semibold text-white">Acceso restringido</h2>
            <p className="mt-1 text-sm text-slate-400">
              Debes iniciar sesión con una cuenta autorizada para editar el catálogo.
            </p>
          </div>
          <a
            href={loginUrl}
            className="inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-emerald-400"
          >
            Iniciar sesión con Google
          </a>
        </div>
      );
    }

    return (
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="catalog-editor" className="mb-2 block text-sm font-medium text-slate-200">
            Catálogo (JSON)
          </label>
          <textarea
            id="catalog-editor"
            className="h-[480px] w-full resize-y rounded-md border border-slate-700 bg-slate-950 font-mono text-sm text-slate-100 shadow-inner shadow-slate-950/20 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            value={editorValue}
            onChange={handleEditorChange}
            spellCheck={false}
            disabled={submitting}
          />
          <p className="mt-2 text-xs text-slate-500">
            Última sincronización: {loading ? "cargando..." : hasEdited ? "editando borrador" : "sin cambios pendientes"}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-emerald-500/60"
          >
            {submitting ? "Guardando..." : "Guardar cambios"}
          </button>
          <button
            type="button"
            onClick={handleFormat}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-slate-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            Formatear JSON
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md border border-rose-500/50 px-4 py-2 text-sm font-medium text-rose-200 transition hover:border-rose-400 hover:text-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Descartar cambios
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-sm uppercase tracking-widest text-slate-400">Panel de administración</p>
            <h1 className="text-2xl font-semibold text-white">Catálogo de Miyogur</h1>
          </div>
          <nav className="flex items-center gap-3 text-sm">
            <a
              className="rounded border border-slate-700 px-3 py-1 text-slate-200 transition hover:border-slate-500 hover:text-white"
              href="/"
            >
              Volver al sitio
            </a>
            {renderHeaderControls()}
          </nav>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-10">
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 shadow-lg shadow-slate-950/30">
          <div className="mb-4 space-y-2">
            <h2 className="text-lg font-semibold text-white">Editar catálogo</h2>
            <p className="text-sm text-slate-400">
              Modifica el JSON y presiona "Guardar cambios" para actualizar el catálogo público. Se
              validará con el backend antes de persistir los cambios.
            </p>
          </div>

          {loading && (
            <div className="mb-4 rounded border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-slate-300">
              Cargando catálogo desde el backend...
            </div>
          )}

          {error && (
            <div className="mb-4 rounded border border-rose-300/40 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
              No se pudo sincronizar con el backend. Se muestra la versión local.
            </div>
          )}

          {authError && (
            <div className="mb-4 rounded border border-rose-300/40 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
              No se pudo verificar la sesión: {authError}
            </div>
          )}

          {status && (
            <div
              className={`mb-4 rounded border px-4 py-3 text-sm ${buildAlertClass(status.type)}`}
            >
              {status.message}
            </div>
          )}

          {renderContent()}
        </section>
      </main>
    </div>
  );
}
